import svgConfig, { BodyConfig } from "@/components/body/parts";
import { PartBase } from "@/components/body/parts/part";
import { AsyncQueue } from "@/components/shared/AsyncQueue";
import util from "@/components/shared/util";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { BodyData, Patient } from "@/store/modules/patient/types";
import _ from "lodash";
import SVG from "svg.js";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";

const SVG_PADDING = 10;

@Component({})
export default class BodyContainer extends Vue {
  @Getter("config", patientNamespace)
  config!: any;

  @Getter("showLegendMobile", navigationNamespace)
  showLegendMobile!: boolean;

  @Getter("minDataPatient", patientNamespace)
  minDataPatient!: boolean;

  @Action("toggleLegendMobile", navigationNamespace)
  toggleLegendMobile!: Function;

  @Action("showPopup", navigationNamespace)
  showPopup!: Function;

  parts: Array<PartBase> = [];
  bodySvg: any;
  wide?: boolean;
  previousCalculatedBox: any = this.defaultBox();

  defaultBox() {
    return {
      y: 0,
      y2: 0,
      x: 0,
      x2: 0
    };
  }

  inProgress: boolean = false;

  @Prop(Object)
  patient!: Patient;

  @Prop(Object)
  breakpoint!: any;

  @Prop(Boolean)
  animated!: boolean;

  @Prop(Boolean)
  isInteractive!: boolean;

  @Prop({
    default: () => {
      return true;
    }
  })
  showInfo!: boolean;

  @Prop(Function)
  popupOverride!: Function;

  @Getter("darkMode", navigationNamespace)
  darkMode!: boolean;

  @Prop(Boolean)
  showIcons!: boolean;

  @Prop(Array)
  removeParts!: Array<any>;

  renderComplete: boolean = false;

  @Watch("patient.body")
  async onDataChange(body: BodyData) {
    if (!body) {
      return;
    }
    const shouldAdjustHeight = this.initialized;
    await this.initialize();

    // Could have been canceled while waiting
    if (!this.initialized) {
      return;
    }

    this.parts.forEach(part => {
      part.updateData(this.patient);
    });
    if (shouldAdjustHeight) {
      this.adjustHeight();
    }
  }

  @Watch("darkMode")
  onDarkChange() {
    this.parts.forEach(part => {
      part.setDark(this.darkMode);
    });
  }

  @Watch("breakpoint")
  onViewChange() {
    if (this.inProgress) {
      return;
    }

    const value = this.$refs.body;
    const originalWide = this.wide;
    if (value instanceof Element) {
      const height = value.clientHeight;
      const width = value.clientWidth;
      if (height < width) {
        if (this.wide !== true) {
          this.wide = true;
          this.parts.forEach(part => {
            part.setWide();
          });
        }
      } else if (this.wide !== false) {
        this.wide = false;
        this.parts.forEach(part => {
          part.setTall();
        });
      }
      if (originalWide !== this.wide) {
        this.adjustHeight();
      }
    }
  }

  private async adjustHeight() {
    let calculatedBox: any = {
      y: Number.MAX_SAFE_INTEGER,
      y2: Number.MIN_SAFE_INTEGER,
      x: Number.MAX_SAFE_INTEGER,
      x2: Number.MIN_SAFE_INTEGER
    };
    for (let i = 0; i < this.parts.length; i++) {
      let part = this.parts[i];
      const box = part.getBox();
      if (box) {
        if (box.y2 > calculatedBox.y2) {
          calculatedBox.y2 = box.y2;
        }
        if (box.x2 > calculatedBox.x2) {
          calculatedBox.x2 = box.x2;
        }
        if (box.x < calculatedBox.x) {
          calculatedBox.x = box.x;
        }
        if (box.y < calculatedBox.y) {
          calculatedBox.y = box.y;
        }
      }
    }
    let outOfBounds = false;

    Object.keys(calculatedBox).forEach(key => {
      if (Math.abs(calculatedBox[key] - this.previousCalculatedBox[key]) > 1) {
        outOfBounds = true;
      }
    });

    this.previousCalculatedBox = calculatedBox;
    if (outOfBounds && this.bodySvg && this.bodySvg.viewbox) {
      this.bodySvg.viewbox(
        calculatedBox.x - SVG_PADDING,
        calculatedBox.y - SVG_PADDING,
        // Need to double padding because it was added in x and y
        calculatedBox.x2 - calculatedBox.x + SVG_PADDING * 2,
        // Don't double bottom calculation if you want it to be flush
        calculatedBox.y2 -
          calculatedBox.y +
          SVG_PADDING * this.config.body.bottomPaddingMultiplier
      );
    }
    this.renderComplete = true;
  }

  hasMounted = false;
  mounted() {
    if (this.minDataPatient && !this.showLegendMobile) {
      this.toggleLegendMobile();
    }
    this.hasMounted = true;
    this.initialize();
  }

  initialized = false;

  async initialize() {
    if (this.initialized || this.dequeue) {
      return Promise.resolve();
    }

    return new Promise(async resolve => {
      if (
        !this.inProgress &&
        this.hasMounted &&
        !this.initialized &&
        _.get(this, "patient.body.version")
      ) {
        this.inProgress = true;

        const bodyRef = this.$refs.body as HTMLElement;
        const body: string = require("!svg-inline-loader!@/assets/svg/body.svg");
        bodyRef.innerHTML = body;

        this.bodySvg = SVG.adopt(bodyRef.firstChild as HTMLElement);
        // This initializes the SVG library to modify paths and other types
        SVG.prepare(bodyRef.firstChild as HTMLElement);

        // Fix for flicker in Safari
        this.bodySvg.style("-webkit-backface-visibility", "hidden");
        this.bodySvg.style("height", "100%");
        this.bodySvg.style("max-width", "100%");
        this.bodySvg.style("margin", "auto");
        this.bodySvg.style("display", "block");

        const { dequeue, promise } = AsyncQueue(
          this.buildBody,
          "body",
          this.patient.id
        );
        this.dequeue = dequeue;
        const result = await promise;
        if (result) {
          this.adjustHeight();

          this.initialized = true;
        }
        this.inProgress = false;
      }

      resolve();
    });
  }

  dequeue: Function | null = null;

  async buildBody(done: Function) {
    const keys = Object.keys(svgConfig);
    for (let i = 0; i < keys.length; i++) {
      let key = keys[i];

      if (!(this.removeParts && this.removeParts.includes(key))) {
        const config = this.appylOverrides(key, svgConfig[key]);
        config.key = key;
        const part = new config.className(config, this.bodySvg, this.$store);
        part.animate(this.animated);

        if (this.isInteractive) {
          const action = this.popupOverride || this.showPopup;
          part.makeInteractive(action);
        }

        part.updateData(this.patient);
        part.setDark(this.darkMode);

        this.parts.push(part);

        await util.animationFrame();
        // Was canceled while waiting, don't continue to loop
        if (!this.dequeue) {
          return;
        }
      }
    }

    if (this.dequeue) {
      this.dequeue = null;
      done();
    }
  }
  appylOverrides(key: string, config: BodyConfig) {
    const patientType = this.patient.census.type;
    if (patientType) {
      const override = _.get(
        this.config,
        `body.configOverrides.${patientType}.${key}`
      );
      if (override) {
        return Object.assign({}, config, override);
      }
    }
    return config;
  }

  beforeDestroy() {
    this.tearnDown();
  }

  tearnDown() {
    if (this.dequeue) {
      this.dequeue();
      this.dequeue = null;
    }
    this.initialized = false;
    this.renderComplete = false;
    this.inProgress = false;
    this.previousCalculatedBox = this.defaultBox();
    this.bodySvg = null;
    this.parts.forEach(part => {
      part.destroy();
    });
    this.parts = [];
  }
}
